import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Info, FormInput, Palette } from "lucide-react";
import AgentFormFieldsTab from "./AgentFormFieldsTab";
import SplashPageCustomizationForm from "./settings/SplashPageCustomizationForm";
import SectionHeader from "../shared/SectionHeader";
import SplashPagePreview from "@/components/previews/SplashPagePreview";
import { useAuth } from "@/contexts/AuthContext";
import { InfoBanner } from "@/components/ui/info-banner";

interface AgentSplashPageTabProps {
  agent: any;
  updateAgent: (data: Partial<any>) => Promise<boolean>;
}

const AgentSplashPageTab = ({
  agent,
  updateAgent,
}: AgentSplashPageTabProps) => {
  const { organization } = useAuth();
  const [previewSettings, setPreviewSettings] = useState({
    title: agent?.splash_page_title || "",
    subtitle: agent?.splash_page_subtitle || "",
    buttonText: agent?.start_button_text || "Start Chat"
  });
  const [previewFields, setPreviewFields] = useState<any[]>([]);
  const [previewContactInfo, setPreviewContactInfo] = useState<any>(null);
  const [previewKey, setPreviewKey] = useState(0);

  const handleFormChange = (values: any) => {
    setPreviewSettings({
      title: values.splash_page_title || "",
      subtitle: values.splash_page_subtitle || "",
      buttonText: values.start_button_text || "Start Chat"
    });
  };

  const handleFieldsChange = (fields: any[]) => {
    setPreviewFields(fields);
  };

  const handleContactInfoChange = (contactInfo: any) => {
    setPreviewContactInfo(contactInfo);
  };

  const handleRefreshPreview = () => {
    // Force preview to refresh by updating its key and resetting state
    setPreviewContactInfo(agent.contact_info);
    setPreviewFields([]);
    setPreviewKey(prev => prev + 1);
  };

  return (
    <div className="w-full max-w-none">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Configuration */}
        <div className="lg:col-span-1 space-y-6">
          {/* Section Explainer */}
          <InfoBanner>
            The splash page introduces your AI agent and captures lead/client info upfront. To collect data conversationally during chat instead, add fields in the form fields section.
          </InfoBanner>

          {/* Pre-Chat Fields Section */}
          <Card className="border rounded-2xl">
            <CardContent className="p-5">
              <SectionHeader
                icon={<FormInput className="h-5 w-5" />}
                title="Fields to Collect Pre Chat"
              />

              <AgentFormFieldsTab 
                agent={agent} 
                fieldSection="pre-session"
                onFieldsChange={handleFieldsChange}
                onContactInfoChange={handleContactInfoChange}
              />
            </CardContent>
          </Card>

          {/* Splash Page Customization Section */}
          <Card className="border rounded-2xl">
            <CardContent className="p-5">
              <SectionHeader
                icon={<Palette className="h-5 w-5" />}
                title="Splash Page Customization"
              />

              <SplashPageCustomizationForm
                agent={agent}
                updateAgent={updateAgent}
                onFormChange={handleFormChange}
              />
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Preview */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            <SplashPagePreview
              key={previewKey}
              agent={{
                ...agent,
                contact_info: previewContactInfo || agent.contact_info
              }}
              organization={organization}
              previewSettings={previewSettings}
              previewFields={previewFields}
              onRefresh={handleRefreshPreview}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentSplashPageTab;
